import { HTMLAttributes, ReactNode } from "react";
import { cn } from "@app/lib/utils";

type ActionGroupAlign = "start" | "center" | "end" | "between";
type ActionGroupMobile = "row" | "stack" | "stack-reverse";
type ActionGroupGap = "compact" | "default";
type ActionGroupStretch = boolean | "mobile" | "always";

export type ActionGroupProps = HTMLAttributes<HTMLDivElement> & {
  align?: ActionGroupAlign;
  gap?: ActionGroupGap;
  mobile?: ActionGroupMobile;
  stretch?: ActionGroupStretch;
  wrap?: boolean;
};

const alignClassName: Record<ActionGroupAlign, string> = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
};

const mobileClassName: Record<ActionGroupMobile, string> = {
  row: "flex-row items-center",
  stack: "flex-col items-center sm:flex-row sm:items-center",
  "stack-reverse": "flex-col-reverse items-center sm:flex-row sm:items-center",
};

const gapClassName: Record<ActionGroupGap, string> = {
  compact: "gap-2",
  default: "gap-2 sm:gap-3",
};

function getStretchClassName(stretch: ActionGroupStretch) {
  if (!stretch) {
    return "";
  }

  if (stretch === "always") {
    return "w-full [&>a:not([data-icon-only=true])]:w-full [&>button:not([data-icon-only=true])]:w-full";
  }

  return "w-full [&>a:not([data-icon-only=true])]:w-full [&>button:not([data-icon-only=true])]:w-full sm:w-auto sm:[&>a]:w-auto sm:[&>button]:w-auto";
}

export function ActionGroup({
  align = "end",
  children,
  className,
  gap = "compact",
  mobile = "row",
  stretch = false,
  wrap = true,
  ...props
}: ActionGroupProps) {
  return (
    <div
      data-action-group="true"
      className={cn(
        "flex min-w-0",
        alignClassName[align],
        mobileClassName[mobile],
        gapClassName[gap],
        wrap && "flex-wrap",
        getStretchClassName(stretch),
        "[&>a]:min-w-0 [&>button]:min-w-0 [&>a]:justify-center [&>button]:justify-center",
        "[&>[data-action-control=true]]:min-w-0 [&>[data-action-control=true]]:justify-center",
        "[&_a]:shadow-none [&_button]:shadow-none",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function FooterActions({
  align = "end",
  children,
  className,
  gap = "default",
  mobile = "row",
  stretch = false,
  wrap = true,
  ...props
}: ActionGroupProps & {
  children: ReactNode;
}) {
  return (
    <ActionGroup
      align={align}
      className={cn(
        align === "end" &&
          (mobile === "stack" || mobile === "stack-reverse") &&
          "items-end sm:items-center",
        className,
      )}
      gap={gap}
      mobile={mobile}
      stretch={stretch}
      wrap={wrap}
      {...props}
    >
      {children}
    </ActionGroup>
  );
}
